import React from "react";
import PropTypes from "prop-types";
import DayPickerInput from "react-day-picker/DayPickerInput";
import { Translate } from "react-thunk-i18nify";

import InputLabel from "components/ReduxForm/InputLabel";
import DotsLoadingIndicator from "components/Spinner/DotsLoadingIndicator";
import FormError from "components/Form/FormError";
import ClearDateIcon from "components/ReduxForm/DateField/ClearDateIcon";

const Loader = ({ show }) => {
  if (!show) return null;

  return (
    <DotsLoadingIndicator className="position-absolute top-50 end-0 me-2" />
  );
};

const renderDateField = ({
  input: { name, value, onBlur, onChange, ...input },
  meta: { error, warning, submitError, touched },
  label,
  labelTranslation,
  disabledDays,
  disabled,
  replacementTranslations,
  isLoadingDate,
  initialMonth,
  inputClassName,
  readOnly,
  datePickerClassNames,
  dataTestId,
}) => {
  const formattedInitialMonth = initialMonth
    ? {
        initialMonth: new Date(
          initialMonth.getFullYear(),
          initialMonth.getMonth()
        ),
      }
    : {};

  const clearDateHandler = () => {
    onChange("");
  };

  return (
    <div data-testid={dataTestId} className="date-field">
      <InputLabel
        name={name}
        label={label}
        labelTranslation={labelTranslation}
        replacementTranslations={replacementTranslations.label}
      />
      <div className="position-relative">
        <DayPickerInput
          id={name}
          value={value && new Date(value)}
          onDayChange={(dateValue) => onChange(dateValue)}
          onBlur={onBlur}
          onChange={onChange}
          dataCyFieldName={`${name}-wrapper`}
          dayPickerProps={{
            todayButton: <Translate value="common.today" />,
            modifiers: {
              disabled: disabledDays,
            },
            ...formattedInitialMonth,
          }}
          inputProps={{
            ...input,
            disabled: disabled || isLoadingDate,
            readOnly: readOnly ?? true,
            className: inputClassName,
            onBlur,
            id: name,
            "data-field-name": name,
            "data-testid": name,
          }}
          key={name}
          classNames={datePickerClassNames}
        />
        <Loader show={isLoadingDate} />
        <ClearDateIcon
          onClick={clearDateHandler}
          value={value}
          disabled={disabled}
        />
      </div>
      <FormError
        touched={touched}
        className="invalid-text"
        error={error}
        submitError={submitError}
        warning={warning}
        name={name}
      />
    </div>
  );
};

Loader.defaultProps = {
  show: false,
};

Loader.propTypes = {
  show: PropTypes.bool,
};

renderDateField.defaultProps = {
  label: undefined,
  disabledDays: undefined,
  disabled: false,
  labelTranslation: undefined,
  replacementTranslations: {
    label: {},
    placeholder: {},
  },
  isLoadingDate: false,
};

renderDateField.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
    warning: PropTypes.string,
  }).isRequired,
  label: PropTypes.string,
  labelTranslation: PropTypes.string,
  disabledDays: PropTypes.oneOfType([
    PropTypes.shape(),
    PropTypes.arrayOf(PropTypes.shape()),
  ]),
  disabled: PropTypes.bool,
  replacementTranslations: PropTypes.shape({
    label: PropTypes.shape({}),
    placeholder: PropTypes.shape({}),
  }),
  isLoadingDate: PropTypes.bool,
  initialMonth: PropTypes.instanceOf(Date),
};

export default renderDateField;
